import { Action } from '@reduxjs/toolkit';
import { Epic, combineEpics } from 'redux-observable';
import {
  EMPTY,
  catchError,
  concatMap,
  filter,
  forkJoin,
  from,
  ignoreElements,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs';

import { eventsAPI } from 'apis/wos';

import { catchAndLogError } from 'utils/common';
import { hasFullyConfiguredCanvas } from 'utils/type_guards';

import module from './index';
import * as api from './cart_product_creator.api';

import canvases from 'domains/canvases';
import optionGroups from 'domains/option_groups';
import activeEditor from 'domains/active_editor';
import cartProducts from 'domains/cart_products';
import textNodes from 'domains/text_nodes';
import multiphotos from 'domains/multiphotos';
import crops from 'domains/crops';

import type { RootState } from 'store/wos_store';

const { actions, selectors } = module;

/**
 * Collect data for standard & multicanvas add to cart
 *
 * The add to cart button to trigger this action should not be enabled
 * if any of the error cases could be reached, so we want to handle those specifically
 *
 * We also nest that within an observable for having a cart guid, which will get set
 * when we create a cart if one doesn't exist yet
 */
const addToCartEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.addToCart.ready.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) =>
      canvases.selectors.selectAll(state).flatMap((canvas) => {
        if (!hasFullyConfiguredCanvas(canvas)) throw new Error('Invalid Product Configuration');
        const cp = canvases.selectors.forCreateCartProduct(state, canvas);
        cp.cart_product_options_attributes = optionGroups.selectors.forCreateCartProduct(
          state,
          canvas
        );

        cp.quantity = selectors.quantity(state);

        if (payload === 'multiphoto') {
          return multiphotos.selectors.forCreateCartProduct(state).map((mp) => ({
            ...cp,
            canvas_rotation: mp.autoRotate ? 90 : 0,
            cart_photos_attributes: [{ ...mp, autoRotate: undefined }],
          }));
        }

        cp.cart_photos_attributes = crops.selectors.forCreateCartProduct(state, canvas);

        // We don't select text nodes by canvas since text node products are disallowed
        // as multi-canvas products. We also can leave it undefined if the array is empty
        const tns = textNodes.selectors.forCreateCartProduct(state);
        if (tns.length > 0) {
          cp.cart_text_nodes_attributes = tns;
        }

        return cp;
      })
    ),
    mergeMap((cps) => forkJoin(cps.map(api.create))),
    map(actions.addToCart.fulfilled),
    catchAndLogError(actions.addToCart.rejected())
  );

/**
 * Save a new cart product & delete the edited one.
 * Same as add to cart, but no need to check for an existing cart or multiphotos.
 */
const saveEditedCartProductEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.saveCartProductEdit.pending.match),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      const canvas = activeEditor.selectors.canvas(state);
      const editingId = canvas?.cartProductId;

      if (!canvas || !hasFullyConfiguredCanvas(canvas) || !editingId)
        throw new Error('Invalid Product Configuration');

      const cp = canvases.selectors.forCreateCartProduct(state, canvas);
      cp.cart_product_options_attributes = optionGroups.selectors.forCreateCartProduct(
        state,
        canvas
      );
      cp.cart_photos_attributes = crops.selectors.forCreateCartProduct(state, canvas);

      // We don't select text nodes by canvas since text node products are disallowed
      // as multi-canvas products. We also can leave it undefined if the array is empty
      const tns = textNodes.selectors.forCreateCartProduct(state);
      if (tns.length > 0) {
        cp.cart_text_nodes_attributes = tns;
      }

      return from(api.create(cp)).pipe(
        concatMap((update) =>
          from(api.destroy(editingId)).pipe(
            map(() => actions.saveCartProductEdit.fulfilled({ update, removeId: editingId })),
            catchAndLogError(actions.saveCartProductEdit.rejected())
          )
        )
      );
    })
  );

/**
 * Fetch cart products after adding to the cart
 */
const addToCartGetCartProductsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.addToCart.fulfilled.match),
    map(() => cartProducts.actions.getAll.pending())
  );

/**
 * Fetch the cart products after editing one
 */
const refreshCartProductsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.saveCartProductEdit.fulfilled.match),
    map(() => cartProducts.actions.getAll.pending())
  );

const sendAddToCartEventEpic: Epic<Action> = (action$) =>
  action$.pipe(
    filter(actions.addToCart.fulfilled.match),
    mergeMap(() => from(eventsAPI.post('', { event: 'AddToCart' })).pipe(catchError(() => EMPTY))),
    ignoreElements()
  );

const combined = combineEpics(
  addToCartEpic,
  saveEditedCartProductEpic,
  addToCartGetCartProductsEpic,
  refreshCartProductsEpic,
  sendAddToCartEventEpic
);
export default combined;
